import React from 'react';
import { FaCheckCircle, FaExclamationCircle, FaQuestionCircle } from 'react-icons/fa';
import { formatToCompactNumber } from '~/libs/utils/format/number';

export interface ProductVersion {
	suklCode: string;
	brand: string;
	description: string;
}

export interface ProductAvailability {
	status: 'available' | 'not-available';
	reasonCode: string;
	ddd: number;
	ddd_share: number;
	odtd: number | null;
	odtd_share: number | null;
	dnc_dou: boolean;
}

export interface Product {
	productVersions: ProductVersion[];
	availability: ProductAvailability;
}

export interface AvailabilityGroup {
	id: string;
	atc_code: string;
	active_substance: string;
	rs: string | null;
	products: Product[];
}

export interface RowProps {
	group: AvailabilityGroup;
	dddShareThreshold: number;
}

function DncDouIcon({ dncDou, status }: { dncDou: boolean; status: 'available' | 'not-available' }) {
	if (!dncDou) {
		return <FaExclamationCircle className='w-4 h-4 text-red-600' />;
	}
	if (status === 'not-available') {
		return <FaQuestionCircle className='w-4 h-4 text-yellow-600' />;
	}
	return <FaCheckCircle className='w-4 h-4 text-green-600' />;
}

function RowTable(title: string) {
	return <span className='block mb-1 text-labelsmall text-gray-500 leading-tight'>{title}</span>;
}

export function Row({ group, dddShareThreshold }: RowProps) {
	const rows = group.products.flatMap((product) => product.productVersions.map((version) => ({ product, version })));

	return (
		<div className='flex flex-nowrap w-full justify-start items-start font-display'>
			<div className='w-64'>
				{RowTable('ATC')}
				<h2 className='text-base font-bold text-gray-900 leading-5 uppercase'>
					<span className='text-cBlue-500 mr-2 tabular-nums'>{group.atc_code}</span>
					{group.active_substance}
				</h2>
			</div>
			<div className='w-14'>
				{RowTable('RS')}
				<h2 className='text-md font-semibold text-gray-900 leading-5 uppercase tabular-nums'>{group.rs}</h2>
			</div>
			<div className='w-auto flex-shrink flex-grow'>
				{rows.map((row, index) => {
					const showBrand = index === 0 || rows[index - 1].version.brand !== row.version.brand;
					const showSeparator = showBrand && index !== 0;
					const showLabel = index === 0;
					return (
						<div
							className={`flex flex-nowrap w-full justify-start items-start gap-x-2${
								showSeparator ? ' pt-3 border-t border-gray-300' : ''
							}${showLabel ? ' pb-3' : ' pb-3'}`}
							key={row.version.suklCode}
						>
							<div className='w-40'>
								{showLabel && RowTable('NÁZEV LP')}
								<h2 className='text-base font-bold text-gray-900 leading-5 uppercase'>
									{showBrand ? row.version.brand : ''}
								</h2>
							</div>
							<div className='w-auto flex-grow flex-shrink'>
								{showLabel && RowTable('DOPLNĚK NÁZVU')}
								<h2 className='text-md font-semibold text-gray-900 leading-5 uppercase tabular-nums'>
									{row.version.description}
								</h2>
							</div>
							<div className='w-16'>
								{showLabel && RowTable('SÚKL kód')}
								<h2 className='text-label font-semibold text-gray-900 leading-5 uppercase tabular-nums'>
									{row.version.suklCode}
								</h2>
							</div>
							<div className='w-52'>
								{showLabel && RowTable('DOSTUPNOST')}
								<div className='flex justify-between items-center w-full text-label text-gray-900 leading-5 uppercase tabular-nums'>
									<span>DDD:</span>
									<strong className='font-bold pr-1.5'>{formatToCompactNumber(row.product.availability.ddd, 2)}</strong>
								</div>
								<div
									className={`relative w-full h-6 leading-6 rounded overflow-hidden ${
										row.product.availability.ddd_share * 100 > dddShareThreshold
											? 'bg-gray-900 text-white'
											: 'bg-gray-300 text-gray-900'
									}`}
								>
									<i
										className={`absolute left-0 top-0 h-full min-w-1.5 rounded ${
											row.product.availability.ddd_share * 100 > dddShareThreshold ? 'bg-cBlue-500' : 'bg-gray-400'
										}`}
										style={{ width: `${(row.product.availability.ddd_share * 100).toFixed(2)}%` }}
									></i>
									<span className='absolute left-0 top-0 w-full h-6 leading-6 px-1.5 text-label font-semibold tabular-nums uppercase text-right'>
										{formatToCompactNumber(row.product.availability.ddd_share * 100)} %
									</span>
								</div>
							</div>
							<div className='w-14 text-center'>
								{showLabel && RowTable('DNC/DoÚ')}
								<div className='pt-6 flex items-center justify-center'>
									<DncDouIcon dncDou={row.product.availability.dnc_dou} status={row.product.availability.status} />
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
